import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { ClipboardEvent, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidInviteEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value)
}

interface InviteEmailInputProps {
  id?: string
  emails: Array<string>
  onChange: (emails: Array<string>) => void
  /** Emails that can't be invited (e.g. the user's own address). */
  exclude?: Array<string>
  disabled?: boolean
}

/**
 * Chip input for invite emails (design turn 14d). Enter, comma, space or
 * blur turns the draft into a chip; Backspace on an empty draft removes the
 * last chip; pasting a list splits it.
 */
export function InviteEmailInput({ id, emails, onChange, exclude = [], disabled }: InviteEmailInputProps) {
  const { t } = useTranslation('auth')
  const [draft, setDraft] = useState('')
  const [invalid, setInvalid] = useState<string | null>(null)

  const commit = (raw: string) => {
    const candidates = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
    if (candidates.length === 0) return true

    const excluded = new Set(exclude.map((e) => e.toLowerCase()))
    const next = [...emails]
    let firstInvalid: string | null = null
    for (const email of candidates) {
      if (!isValidInviteEmail(email)) {
        firstInvalid ??= email
        continue
      }
      if (!next.includes(email) && !excluded.has(email)) next.push(email)
    }
    if (next.length !== emails.length) onChange(next)
    setInvalid(firstInvalid)
    setDraft(firstInvalid ?? '')
    return firstInvalid === null
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
      if (e.key === 'Tab' && !draft.trim()) return
      if (draft.trim()) {
        e.preventDefault()
        commit(draft)
      } else if (e.key !== 'Tab') {
        e.preventDefault()
      }
    } else if (e.key === 'Backspace' && draft === '' && emails.length > 0) {
      onChange(emails.slice(0, -1))
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (/[\s,;]/.test(text.trim())) {
      e.preventDefault()
      commit(`${draft} ${text}`)
    }
  }

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-input bg-card px-2 py-1.5 transition-[border-color,box-shadow]',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          invalid && 'border-destructive',
        )}
      >
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-secondary py-0.5 pl-2 pr-1 text-[13px] text-secondary-foreground"
          >
            <span className="truncate">{email}</span>
            <button
              type="button"
              onClick={() => onChange(emails.filter((e) => e !== email))}
              disabled={disabled}
              className="flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('onboarding.invite.remove', { email })}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          inputMode="email"
          autoComplete="off"
          value={draft}
          disabled={disabled}
          placeholder={emails.length === 0 ? t('onboarding.invite.placeholder') : ''}
          onChange={(e) => {
            setDraft(e.target.value)
            if (invalid) setInvalid(null)
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => commit(draft)}
          aria-invalid={Boolean(invalid) || undefined}
          className="h-7 min-w-[140px] flex-1 bg-transparent px-1 text-base outline-none placeholder:text-muted-foreground md:text-[15px]"
        />
      </div>
      {invalid ? (
        <p role="alert" className="text-sm text-destructive">
          {t('onboarding.invite.invalid', { email: invalid })}
        </p>
      ) : null}
    </div>
  )
}
